import { AccountRole } from '@/data/usecases/account/account-role'
import { AccountType } from '@/data/usecases/account/account-type'
import { AccountCompanyModel } from '@/domain/models/account/account-company-model'
import { AccountPersonalProfileModel } from '@/domain/models/account/account-personal-profile-model'
import { AccountProfileModel } from '@/domain/models/account/account-profile-model'
import { GenericResponse } from '@/infra/http'
import {
  AccountAddonModel,
  AccountBasicModel,
  AccountModel
} from '../../models/account/account-model'
import { AccountAnonymous } from './account-anonymous'
import { AccountBenefit } from './account-benefit'

// ============================================
// MÓDULO DA CONTA (conceito de domínio — V3)
// ============================================
//
// Formaliza no domínio o eixo "a que parte da aplicação esta conta pertence",
// que antes existia apenas como convenção de pastas (V1/V2) e como checagem
// role-a-role espalhada pela aplicação.
//
// - `AccountModule`: eixo exclusivo (uma conta pertence a um módulo).
// - `BackofficePersona`: tipo de usuário dentro do backoffice. É MEMBERSHIP (não-exclusivo):
//   uma conta pode ter mais de uma persona ao mesmo tempo
//   (ex.: company manager que também é internal company manager). Por isso a
//   derivação (`Account.getPersonas`) retorna uma lista, espelhando 1:1 o
//   comportamento atual da navegação e dos guards (flags independentes).
//
// ============================================
// MODELO DE ACESSO (leia antes de gatear rotas por módulo)
// ============================================
//
// O acesso a rotas usa DOIS critérios distintos — não confundir:
//
//   • BACKOFFICE → gate por PERSONA (`getPersonas`, via `requirePersona(...)`).
//   • USER       → gate por CAPABILITY: posse de pass plan (`Account.hasPassPlan`,
//                  via `userGuards.passPlanHolder`). NÃO se gateia por módulo.
//
// Por que USER não é gateado por módulo: `AccountModule` é EXCLUSIVO, mas as
// personas reais SE SOBREPÕEM. Um company-admin ou internal manager que também
// possui pass plan é classificado como BACKOFFICE (precedência), porém usa
// legitimamente as telas de USER (home, perfil, benefício...). Se o acesso a USER
// dependesse do módulo, esse gestor-com-plano seria bloqueado indevidamente — por
// isso o critério é a posse do plano, não o módulo.
//
// `getModule` permanece a fonte para CLASSIFICAÇÃO (backoffice/persona, nav e um
// eventual roteamento por módulo no router). Não é critério de acesso a USER.

export enum AccountModule {
  USER = 'user',
  BACKOFFICE = 'backoffice',
  AUTH = 'auth'
}

export enum BackofficePersona {
  /** Admin do cliente — o "RH" (Company Manager). */
  COMPANY_ADMIN = 'company-admin',
  /** Superadmin — ops interno Unico (Internal Company Manager). */
  INTERNAL = 'internal'
}

export interface Account {
  get(): Promise<Account.Model>
  loaderGet(): Promise<Account.Model>
  profile(): Promise<Account.ProfileModel>
  updateProfile(accountProfile: Account.ProfileModel): Promise<GenericResponse<void>>
  loader(): Promise<Account.ProfileModel>
  updatePersonalProfile(accountProfile: AccountPersonalProfileModel): Promise<GenericResponse<void>>
  updatePicture(accountId: string, file: File): Promise<Account.PictureModel>
  removePicture(accountId: string): Promise<void>
  getBenefits(accountId: string): Promise<AccountBenefit.Model>
}

export namespace Account {
  export type Model = AccountModel
  export type BasicModel = AccountBasicModel
  export type ProfileModel = AccountProfileModel
  export type CompanyModel = AccountCompanyModel
  export type PersonalProfileModel = AccountPersonalProfileModel
  export type PictureModel = { pictureUrl: string }
  export type AddonModel = AccountAddonModel

  export const Key = 'account'
  export const ProfileKey = 'account-profile'
  export const UpdateProfileKey = 'update-account-profile'
  export const PictureKey = 'account-picture-key'

  export const isAnonymous = (accountProfile: Account.Model) =>
    accountProfile.id === AccountAnonymous.ID
  export const getAnonymous = () => AccountAnonymous.Model
  export const hasPassPlan = (accountProfile: Account.Model) => accountProfile.passPlan !== null
  export const isInternalCompanyManager = (accountProfile: Account.Model) =>
    AccountRole.contains(AccountRole.Enum.INTERNAL_COMPANY_MANAGER, accountProfile.roles)
  export const isCompanyManager = (accountProfile: Account.Model) =>
    AccountRole.contains(AccountRole.Enum.COMPANY_MANAGER, accountProfile.roles)
  export const isDependentUser = (accountProfile: Account.Model) =>
    accountProfile.accountType === AccountType.Enum.DEPENDENT ||
    AccountRole.contains(AccountRole.Enum.USER_DEPENDENT, accountProfile.roles)
  export const isTeamManager = (accountProfile: Account.Model) =>
    AccountRole.contains(AccountRole.Enum.TEAM_MANAGER, accountProfile.roles)
  export const isSystemManager = (accountProfile: Account.Model) =>
    AccountRole.contains(AccountRole.Enum.SYSTEM_MANAGER, accountProfile.roles)
  export const isCollaborator = (accountProfile: Account.Model) =>
    accountProfile.accountType === AccountType.Enum.COLLABORATOR

  /**
   * Evaluates the account profile and returns an object containing boolean flags
   * indicating the various roles and permissions associated with the account.
   *
   * @param accountProfile - The account model to evaluate roles for.
   * @returns An object with boolean properties for each role/permission.
   * @example
   * const roles = getRoles(accountProfile);
   * if (roles.isCompanyManager) {
   *   // Handle company manager logic
   * }
   */
  export const getRoles = (accountProfile: Account.Model) => {
    const collaborator = isCollaborator(accountProfile)
    const dependent = isDependentUser(accountProfile)
    const companyManager = isCompanyManager(accountProfile)
    const teamManager = isTeamManager(accountProfile)
    const internalCompanyManager = isInternalCompanyManager(accountProfile)
    const systemManager = isSystemManager(accountProfile)
    const anonymous = isAnonymous(accountProfile)

    return {
      // atomic role flags
      isCollaborator: collaborator,
      isDependentUser: dependent,
      isCompanyManager: companyManager,
      isTeamManager: teamManager,
      isInternalCompanyManager: internalCompanyManager,
      isSuperAdmin: internalCompanyManager,
      isSystemManager: systemManager,
      isAnonymous: anonymous,

      // derived visibility flags
      hasBenefitMenu: collaborator || dependent
    }
  }

  /**
   * Deriva o MÓDULO da conta a partir das roles (eixo exclusivo de CLASSIFICAÇÃO).
   *
   * Precedência: BACKOFFICE > USER > AUTH.
   * - `AUTH`      — conta anônima.
   * - `BACKOFFICE`— qualquer perfil administrativo (company/team/internal/system manager).
   * - `USER`      — fluxos normais (colaborador/dependente) e fallback.
   *
   * É a fonte única para "a que parte da aplicação esta conta pertence",
   * substituindo a checagem role-a-role espalhada.
   *
   * ⚠️ ESCOPO — o que este eixo governa (e o que NÃO governa):
   * - GOVERNA: nav/derivação de personas do BACKOFFICE (`getPersonas`, via
   *   `requirePersona(...)`) e um eventual roteamento por módulo no router.
   * - NÃO GOVERNA: acesso às telas do módulo USER. Estas são gateadas por
   *   CAPABILITY (`hasPassPlan`, via `userGuards.passPlanHolder`), nunca por
   *   módulo. Motivo: o eixo é exclusivo, mas as personas reais se sobrepõem —
   *   um gestor (classificado como BACKOFFICE por precedência) com plano ativo é,
   *   legitimamente, também usuário das telas de benefício. Condicionar o acesso a
   *   USER ao módulo bloquearia indevidamente esse gestor-com-plano.
   *
   * Resumindo: BACKOFFICE gateia por persona; USER gateia por plano.
   * Ver os tipos `AccountModule`/`BackofficePersona` no topo deste arquivo e `userGuards`.
   */
  export const getModule = (accountProfile: Account.Model): AccountModule => {
    if (isAnonymous(accountProfile)) {
      return AccountModule.AUTH
    }

    if (
      isCompanyManager(accountProfile) ||
      isTeamManager(accountProfile) ||
      isInternalCompanyManager(accountProfile) ||
      isSystemManager(accountProfile)
    ) {
      return AccountModule.BACKOFFICE
    }

    return AccountModule.USER
  }

  /**
   * Deriva as PERSONAS de backoffice da conta (membership, NÃO exclusivo).
   *
   * Uma conta pode ter mais de uma persona ao mesmo tempo — espelha
   * 1:1 as flags independentes hoje usadas pela navegação e pelos guards:
   * - `COMPANY_ADMIN` ⟸ company manager (o "RH")
   * - `INTERNAL`      ⟸ internal company manager (superadmin / ops interno)
   *
   * Retorna `[]` para contas fora do módulo backoffice. Observação: system
   * manager e team manager são backoffice porém não compõem persona (seguem
   * com guards/seções dedicados por role), preservando o comportamento atual.
   */
  export const getPersonas = (accountProfile: Account.Model): BackofficePersona[] => {
    if (getModule(accountProfile) !== AccountModule.BACKOFFICE) {
      return []
    }

    const personas: BackofficePersona[] = []
    if (isCompanyManager(accountProfile)) {
      personas.push(BackofficePersona.COMPANY_ADMIN)
    }
    if (isInternalCompanyManager(accountProfile)) {
      personas.push(BackofficePersona.INTERNAL)
    }

    return personas
  }
}
